import React, { useState, useEffect } from 'react';
import styles from './SchedulingFlow.module.css';
import { getPatientAppointments, cancelAppointment } from '../../utils/apis';
import { getUserFriendlyErrorMessage } from '../../utils/errorHandler';
import bgImg from './SVGs/image001.png';

interface ExistingAppointment {
  appointmentid: string;
  patientid: string;
  providerid: string;
  departmentid: string;
  appointmenttypeid: string;
  scheduleddatetime: string;
  date: string;
  starttime: string;
  duration: number;
  appointmenttype: string;
  appointmentstatus: string;
  patientappointmenttypename?: string;
  providerDetails?: {
    providerid: number;
    firstname: string;
    lastname: string;
    displayname: string;
    schedulingname: string;
    specialty: string;
    entitytype: string;
    providertype: string;
    billable: boolean;
    status: string;
  };
  // Legacy fields for compatibility
  athenaAppointmentId?: string;
  athenaPatientId?: string;
  athenaProviderId?: string;
  athenaDepartmentId?: string;
  appointmentTypeId?: string;
  startTime?: string;
  endTime?: string;
  durationMinutes?: number;
  reason?: string;
  reasonType?: string;
  status?: string;
  providerName?: string;
  departmentName?: string;
}

interface ExistingAppointmentsStepProps {
  onBack: () => void;
  onReschedule: (appointment: ExistingAppointment) => void;
  onNewAppointment: () => void;
  patientId: string;
}

const ExistingAppointmentsStep: React.FC<ExistingAppointmentsStepProps> = ({
  onBack,
  onReschedule,
  onNewAppointment,
  patientId
}) => {
  const [appointments, setAppointments] = useState<ExistingAppointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState<ExistingAppointment | null>(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [showCancelSuccess, setShowCancelSuccess] = useState(false);
  const [cancelledAppointment, setCancelledAppointment] = useState<ExistingAppointment | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [cancellationError, setCancellationError] = useState<string | null>(null);

  // Header/Nav helpers (same as other screens)
  const NAV_PHONE = '(720) 400-7025';
  const NAV_LINKS = [
    { label: 'BOOK ONLINE', href: '#', action: 'refresh' },
  ];
  const NAV_LINKS_MOBILE = [
    { label: 'BOOK ONLINE', href: '#', action: 'refresh' },
  ];
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 700);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const currentNavLinks = isMobile ? NAV_LINKS_MOBILE : NAV_LINKS;

  const handleNavClick = (action: string) => {
    if (action === 'refresh') {
      // Refresh the page to go back to landing
      window.location.reload();
    }
  };

  const fetchAppointments = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPatientAppointments(patientId);
      setAppointments(data || []);
    } catch (err: any) {
      const userFriendlyError = getUserFriendlyErrorMessage(err, 'loading appointments');
      setError(userFriendlyError);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  React.useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const handleCancelClick = (appointment: ExistingAppointment) => {
    setAppointmentToCancel(appointment);
    setCancellationReason('');
    setShowCancelConfirm(true);
  };

  const handleCancelConfirm = async () => {
    if (!appointmentToCancel) return;

    try {
      setCancellingId(appointmentToCancel.appointmentid);
      
      // Check if patientId is missing and use the prop patientId as fallback
      const patientIdToUse = appointmentToCancel.patientid || patientId;
      
      await cancelAppointment({
        appointmentId: appointmentToCancel.appointmentid,
        patientId: patientIdToUse,
        departmentId: appointmentToCancel.departmentid,
        providerId: appointmentToCancel.providerid,
        reason: cancellationReason || 'Patient request'
      });
      
      setCancelledAppointment(appointmentToCancel);
      setShowCancelConfirm(false);
      setAppointmentToCancel(null);
      setCancellationReason('');
      setShowCancelSuccess(true);
    } catch (err: any) {
      const userFriendlyError = getUserFriendlyErrorMessage(err, 'cancelling appointment');
      setCancellationError(userFriendlyError);
      setShowErrorModal(true);
      setShowCancelConfirm(false); // Close the cancel confirmation modal
    } finally {
      setCancellingId(null);
    }
  };

  const formatDateTime = (appointment: ExistingAppointment) => {
    try {
      // Prioritize separate date and starttime fields from API response
      if (appointment.date && appointment.starttime) {
        const dateStr = appointment.date; // "09/15/2025"
        const timeStr = appointment.starttime; // "09:00"
        const [month, day, year] = dateStr.split('/');
        const [hour, minute] = timeStr.split(':');
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute));
      }
      
      // Fallback to scheduleddatetime if separate fields are not available
      if (appointment.scheduleddatetime) {
        const date = new Date(appointment.scheduleddatetime);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
      
      // Final fallback
      return new Date();
    } catch (error) {
      // Fallback to current date if parsing fails
      return new Date();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'CONFIRMED':
      case 'F': // 'f' appears to be confirmed status in the API
        return '#10b981'; // green
      case 'RESCHEDULED':
        return '#f59e0b'; // amber
      case 'CANCELLED':
      case 'C':
        return '#ef4444'; // red
      default:
        return '#6b7280'; // gray
    }
  };

  const getStatusText = (status: string) => {
    switch (status.toUpperCase()) {
      case 'F':
        return 'CONFIRMED';
      case 'C':
        return 'CANCELLED';
      default:
        return status;
    }
  };

  const getProviderDisplayName = (appointment: ExistingAppointment) => {
    // Use providerDetails if available (new API response)
    if (appointment.providerDetails?.firstname && appointment.providerDetails?.lastname) {
      return `${appointment.providerDetails.firstname} ${appointment.providerDetails.lastname}`;
    }
    
    // Fallback to displayname if available
    if (appointment.providerDetails?.displayname) {
      return appointment.providerDetails.displayname;
    }
    
    // Fallback to legacy providerName field
    if (appointment.providerName) {
      return appointment.providerName;
    }
    
    // Final fallback to appointment type name
    return appointment.patientappointmenttypename || appointment.appointmenttype || 'Appointment';
  };

  if (loading) {
    return (
      <div className={styles.landingRoot}>
        {/* Top Banner with Background Image and Overlay */}
        <div className={styles.topBanner}>
          <img src={bgImg} alt="Banner" className={styles.bannerImg} />
          <div className={styles.bannerOverlay} />
          {/* Nav Bar */}
          <div className={styles.navBar}>
            <span className={styles.navPhone}>{NAV_PHONE}</span>
            <div className={styles.navLinks}>
              {currentNavLinks.map(link => (
                <button key={link.label} className={styles.navLink} onClick={() => handleNavClick(link.action)}>{link.label}</button>
              ))}
            </div>
          </div>
          {/* Banner Content */}
          <div className={styles.bannerContent}>
            <h1 className={styles.bannerTitle}>Your Existing Appointments</h1>
            <div className={styles.bannerSubtitle}>
              Manage your existing appointments or book a new one.
            </div>
          </div>
        </div>

        {/* Main Content with Loading State */}
        <div className={styles.landingMain}>
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}>
              <div className={styles.spinner}></div>
            </div>
            <h2 className={styles.loadingTitle}>Loading Your Appointments...</h2>
            <p className={styles.loadingSubtitle}>Please wait while we fetch your existing appointments.</p>
          </div>
        </div>

        {/* Footer */}
        <footer className={styles.landingFooter}>
          <div className={styles.footerContent}>
            <div className={styles.footerTitle}>Axis Integrated Mental Health</div>
            <div className={styles.footerContact}>Phone (appointments): 720-400-7025</div>
            <div className={styles.footerRating}>
              <span className={styles.footerStars}>★★★★★</span>
              <span className={styles.footerAvg}>Average Rating: 4.9/5</span>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.landingRoot}>
        {/* Top Banner with Background Image and Overlay */}
        <div className={styles.topBanner}>
          <img src={bgImg} alt="Banner" className={styles.bannerImg} />
          <div className={styles.bannerOverlay} />
          {/* Nav Bar */}
          <div className={styles.navBar}>
            <span className={styles.navPhone}>{NAV_PHONE}</span>
            <div className={styles.navLinks}>
              {currentNavLinks.map(link => (
                <button key={link.label} className={styles.navLink} onClick={() => handleNavClick(link.action)}>{link.label}</button>
              ))}
            </div>
          </div>
          {/* Banner Content */}
          <div className={styles.bannerContent}>
            <h1 className={styles.bannerTitle}>Your Existing Appointments</h1>
            <div className={styles.bannerSubtitle}>
              Manage your existing appointments or book a new one.
            </div>
          </div>
        </div>

        {/* Main Content with Error State */}
        <div className={styles.landingMain}>
          <div className={styles.errorContainer}>
            <h2 className={styles.errorTitle}>Error Loading Appointments</h2>
            <p className={styles.errorSubtitle}>{error}</p>
            <button onClick={onBack} className={styles.backButton}>
              ← Back
            </button>
          </div>
        </div>

        {/* Footer */}
        <footer className={styles.landingFooter}>
          <div className={styles.footerContent}>
            <div className={styles.footerTitle}>Axis Integrated Mental Health</div>
            <div className={styles.footerContact}>Phone (appointments): 720-400-7025</div>
            <div className={styles.footerRating}>
              <span className={styles.footerStars}>★★★★★</span>
              <span className={styles.footerAvg}>Average Rating: 4.9/5</span>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  if (showCancelSuccess) {
    return (
      <div className={styles.landingRoot}>
        {/* Top Banner with Background Image and Overlay */}
        <div className={styles.topBanner}>
          <img src={bgImg} alt="Banner" className={styles.bannerImg} />
          <div className={styles.bannerOverlay} />
          {/* Nav Bar */}
          <div className={styles.navBar}>
            <span className={styles.navPhone}>{NAV_PHONE}</span>
            <div className={styles.navLinks}>
              {currentNavLinks.map(link => (
                <button key={link.label} className={styles.navLink} onClick={() => handleNavClick(link.action)}>{link.label}</button>
              ))}
            </div>
          </div>
          {/* Banner Content */}
          <div className={styles.bannerContent}>
            <h1 className={styles.bannerTitle}>Appointment Cancelled!</h1>
            <div className={styles.bannerSubtitle}>
              Your appointment has been successfully cancelled
            </div>
          </div>
        </div>

        {/* Main Content with Success State */}
        <div className={styles.landingMain}>
          <div className={styles.successContainer}>
            <div className={styles.successIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#10b981" stroke="#10b981" strokeWidth="2"/>
                <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            
            <h2 className={styles.successTitle}>Cancellation Confirmed!</h2>
            <p className={styles.successSubtitle}>
              Your appointment has been successfully cancelled. You will receive a confirmation email shortly.
            </p>
            
            {cancelledAppointment && (
              <div className={styles.confirmationDetails}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Cancelled Appointment:</span>
                  <span className={styles.detailValue}>
                    {getProviderDisplayName(cancelledAppointment)} - {formatDateTime(cancelledAppointment).toLocaleString([], { 
                      dateStyle: 'full', 
                      timeStyle: 'short' 
                    })}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>What's Next:</span>
                  <span className={styles.detailValue}>
                    Check your email for cancellation confirmation. You can book a new appointment anytime.
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Need to Reschedule?</span>
                  <span className={styles.detailValue}>
                    If you need to reschedule instead of cancel, please contact us or book a new appointment.
                  </span>
                </div>
              </div>
            )}

            <div className={styles.actionButtons}>
              <button
                className={styles.primaryButton}
                onClick={onNewAppointment}
              >
                Book New Appointment
              </button>
              <button
                className={styles.secondaryButton}
                onClick={onBack}
              >
                Return to Home
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className={styles.landingFooter}>
          <div className={styles.footerContent}>
            <div className={styles.footerTitle}>Axis Integrated Mental Health</div>
            <div className={styles.footerContact}>Phone (appointments): 720-400-7025</div>
            <div className={styles.footerRating}>
              <span className={styles.footerStars}>★★★★★</span>
              <span className={styles.footerAvg}>Average Rating: 4.9/5</span>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className={styles.landingRoot}>
      {/* Top Banner with Background Image and Overlay */}
      <div className={styles.topBanner}>
        <img src={bgImg} alt="Banner" className={styles.bannerImg} />
        <div className={styles.bannerOverlay} />
        {/* Nav Bar */}
        <div className={styles.navBar}>
          <span className={styles.navPhone}>{NAV_PHONE}</span>
          <div className={styles.navLinks}>
            {currentNavLinks.map(link => (
              <button key={link.label} className={styles.navLink} onClick={() => {}}>{link.label}</button>
            ))}
          </div>
        </div>
        {/* Banner Content */}
        <div className={styles.bannerContent}>
          <h1 className={styles.bannerTitle}>Your Existing Appointments</h1>
          <div className={styles.bannerSubtitle}>
            Manage your existing appointments or book a new one.
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.landingMain}>
        {appointments.length === 0 ? (
          <div className={styles.card}>
            <div className={styles.emptyState}>
              <h3>No Appointments Found</h3>
              <p>You don't have any existing appointments.</p>
              <button onClick={onNewAppointment} className={styles.primaryButton}>
                Book New Appointment
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.appointmentsContainer}>
            {appointments.map((appointment) => (
              <div key={appointment.appointmentid} className={styles.appointmentCard}>
                <div className={styles.appointmentHeader}>
                  <div className={styles.appointmentInfo}>
                    <h3 className={styles.appointmentTitle}>
                      {getProviderDisplayName(appointment)}
                    </h3>
                    <div className={styles.appointmentDateTime}>
                      {formatDateTime(appointment).toLocaleString([], { 
                        dateStyle: 'full', 
                        timeStyle: 'short' 
                      })}
                    </div>
                    <div className={styles.appointmentDuration}>
                      Duration: {appointment.duration} minutes
                    </div>
                  </div>
                  <div 
                    className={styles.appointmentStatus}
                    style={{ color: getStatusColor(appointment.appointmentstatus) }}
                  >
                    {getStatusText(appointment.appointmentstatus)}
                  </div>
                </div>

                <div className={styles.appointmentActions}>
                  <button
                    onClick={() => onReschedule(appointment)}
                    className={styles.secondaryButton}
                    disabled={appointment.appointmentstatus === 'C' || appointment.appointmentstatus === 'CANCELLED'}
                  >
                    Reschedule
                  </button>
                  <button
                    onClick={() => handleCancelClick(appointment)}
                    className={styles.dangerButton}
                    disabled={appointment.appointmentstatus === 'C' || appointment.appointmentstatus === 'CANCELLED' || cancellingId === appointment.appointmentid}
                  >
                    {cancellingId === appointment.appointmentid ? 'Cancelling...' : 'Cancel'}
                  </button>
                </div>
              </div>
            ))}

            <div className={styles.newAppointmentSection}>
              <button onClick={onNewAppointment} className={styles.primaryButton}>
                Book New Appointment
              </button>
            </div>
          </div>
        )}

        <div className={styles.actions}>
          <button onClick={onBack} className={styles.backButton}>
            ← Back
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className={styles.landingFooter}>
        <div className={styles.footerContent}>
          <div className={styles.footerTitle}>Axis Integrated Mental Health</div>
          <div className={styles.footerContact}>Phone (appointments): 720-400-7025</div>
          <div className={styles.footerRating}>
            <span className={styles.footerStars}>★★★★★</span>
            <span className={styles.footerAvg}>Average Rating: 4.9/5</span>
          </div>
        </div>
      </footer>

      {/* Confirmation Modal */}
      {showCancelConfirm && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>Cancel Appointment</h3>
            <p className={styles.modalText}>
              Are you sure you want to cancel your appointment with {appointmentToCancel && getProviderDisplayName(appointmentToCancel)}? 
              This action cannot be undone.
            </p>
            
            <div className={styles.reasonSection}>
              <label className={styles.reasonLabel}>
                Reason for cancellation (optional):
              </label>
              <textarea
                className={styles.reasonInput}
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Please provide a reason for cancelling this appointment..."
                rows={3}
                maxLength={500}
              />
            </div>
            
            <div className={styles.modalButtons}>
              <button
                className={styles.modalButtonSecondary}
                onClick={() => {
                  setShowCancelConfirm(false);
                  setAppointmentToCancel(null);
                  setCancellationReason('');
                }}
                disabled={cancellingId !== null}
              >
                Keep Appointment
              </button>
              <button
                className={styles.modalButtonPrimary}
                onClick={handleCancelConfirm}
                disabled={cancellingId !== null}
              >
                {cancellingId !== null ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>We ran into an issue</h3>
            <p className={styles.modalText}>{cancellationError}</p>
            <div className={styles.modalButtons}>
              <button
                className={styles.modalButtonPrimary}
                onClick={() => {
                  setShowErrorModal(false);
                  setCancellationError(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExistingAppointmentsStep;
